import { useEffect, useRef, useState } from "react";
import {
  App,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import errorCodes from "@/apis/error";
import { fetch as fetchEquipment, save as saveEquipment } from "@/apis/equipment";
import { CurrencySelect, DepartmentSelect } from "@/components/Selector";
import { RootState } from "@/store";
import {
  Equipment,
  EquipmentCategory,
  EquipmentSaveParams,
} from "@/types/equipment";

// 设备表单组件参数
interface EquipmentFormProps {
  open: boolean; // 弹窗是否打开
  category: EquipmentCategory; // 新增设备所属的当前类目
  equipmentId?: string; // 编辑时的设备 ID
  equipmentCategoryId?: string; // 编辑设备原有的类目 ID
  onCancel: () => void; // 关闭表单
  onSaved: (equipment: Equipment) => void; // 保存成功回调
}

// 设备表单字段，其中日期和币种使用组件需要的值类型
interface EquipmentFormValues {
  name: string;
  brand?: string | null;
  model?: string | null;
  serialNo?: string | null;
  keeperOrgId: string;
  orgId?: string | null;
  location?: string | null;
  placeLocation?: string | null;
  maintainDays?: number | null;
  maintainThreshold?: number | null;
  purchaseAt?: Dayjs | null;
  purchasePrice?: number | null;
  purchaseCurrency?: string | null;
  supplier?: string | null;
}

// 清理可选文本，空字符串统一提交为 null
const optionalText = (value?: string | null) => value?.trim() || null;

// 将设备详情转换为表单组件可识别的值
const toFormValues = (equipment: Equipment): EquipmentFormValues => ({
  name: equipment.name,
  brand: equipment.brand,
  model: equipment.model,
  serialNo: equipment.serialNo,
  keeperOrgId: equipment.keeperOrgId,
  orgId: equipment.orgId,
  location: equipment.location,
  placeLocation: equipment.placeLocation,
  maintainDays: equipment.maintainDays,
  maintainThreshold: equipment.maintainThreshold,
  purchaseAt: equipment.purchaseAt ? dayjs(equipment.purchaseAt) : null,
  purchasePrice: equipment.purchasePrice,
  purchaseCurrency: equipment.purchaseCurrency === null
    ? null
    : String(equipment.purchaseCurrency),
  supplier: equipment.supplier,
});

// 新增或编辑设备，并负责详情回填与保存
const EquipmentForm = ({
  open,
  category,
  equipmentId,
  equipmentCategoryId,
  onCancel,
  onSaved,
}: EquipmentFormProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const currentOrg = useSelector(
    (state: RootState) => state.orgReducer.curOrg,
  );
  const [form] = Form.useForm<EquipmentFormValues>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailReady, setDetailReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const detailControllerRef = useRef<AbortController>(); // 当前详情请求控制器

  useEffect(() => {
    // 切换设备或关闭弹窗时取消上一条详情请求
    detailControllerRef.current?.abort();

    if (!open) {
      form.resetFields();
      setDetailLoading(false);
      setDetailReady(false);
      return;
    }

    form.resetFields();
    if (!equipmentId) {
      // 新增设备时默认使用当前组织作为保管部门
      setDetailReady(true);
      form.setFieldsValue({
        name: "",
        keeperOrgId: currentOrg.id || undefined,
        purchaseAt: null,
        purchaseCurrency: null,
      });
      return;
    }

    const controller = new AbortController();
    detailControllerRef.current = controller;
    setDetailLoading(true);
    setDetailReady(false);
    fetchEquipment(equipmentId, controller)
      .then((equipment) => {
        // 仅允许最新请求回填表单，避免快速切换时数据错位
        if (detailControllerRef.current === controller) {
          form.setFieldsValue(toFormValues(equipment));
          setDetailReady(true);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          notification.error({
            message: t("equipment.equipmentFetchFailed"),
            description: errorCodes[Number(error)] ?? String(error),
          });
        }
      })
      .finally(() => {
        if (detailControllerRef.current === controller) {
          setDetailLoading(false);
        }
      });

    return () => controller.abort();
  }, [currentOrg.id, equipmentId, form, notification, open, t]);

  const handleSubmit = async () => {
    let values: EquipmentFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const purchaseCurrency = values.purchaseCurrency
      ? Number(values.purchaseCurrency)
      : null;
    // 编辑时沿用设备原类目，新增时使用当前选中类目
    const categoryId = equipmentId
      ? equipmentCategoryId
      : category.id;
    if (!categoryId) {
      return;
    }
    const body: EquipmentSaveParams = {
      id: equipmentId ?? null,
      name: values.name.trim(),
      categoryId,
      brand: optionalText(values.brand),
      model: optionalText(values.model),
      serialNo: optionalText(values.serialNo),
      keeperOrgId: values.keeperOrgId,
      orgId: values.orgId || null,
      location: optionalText(values.location),
      placeLocation: optionalText(values.placeLocation),
      maintainDays: values.maintainDays ?? null,
      maintainThreshold: values.maintainThreshold ?? null,
      purchaseAt: values.purchaseAt?.valueOf() ?? null,
      purchasePrice: values.purchasePrice ?? null,
      purchaseCurrency: Number.isNaN(purchaseCurrency)
        ? null
        : purchaseCurrency,
      supplier: optionalText(values.supplier),
    };

    setSaving(true);
    try {
      const equipment = await saveEquipment(body);
      notification.success({
        message: equipmentId
          ? t("equipment.editEquipmentSucceeded")
          : t("equipment.addEquipmentSucceeded"),
      });
      onSaved(equipment);
    } catch (error) {
      notification.error({
        message: t("equipment.equipmentSaveFailed"),
        description: errorCodes[Number(error)] ?? String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={equipmentId
        ? t("equipment.editEquipment")
        : t("equipment.addEquipment")}
      open={open}
      width={900}
      maskClosable={false}
      destroyOnClose
      confirmLoading={saving}
      okButtonProps={{ disabled: detailLoading || !detailReady }}
      styles={{
        content: { padding: "16px 20px" },
        header: { marginBottom: 8 },
        footer: { marginTop: 8 },
      }}
      okText={t("common.confirm")}
      cancelText={t("common.cancel")}
      onCancel={onCancel}
      onOk={handleSubmit}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={detailLoading}
        style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label={t("equipment.equipmentName")}
              name="name"
              rules={[{
                required: true,
                whitespace: true,
                message: t("equipment.equipmentNameRequired"),
              }]}
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.serialNo")}
              name="serialNo"
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.brand")}
              name="brand"
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.model")}
              name="model"
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.keeperOrg")}
              name="keeperOrgId"
              rules={[{
                required: true,
                message: t("equipment.keeperOrgRequired"),
              }]}
              style={{ marginBottom: 12 }}
            >
              <DepartmentSelect allowClear={false} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.ownedOrg")}
              name="orgId"
              style={{ marginBottom: 12 }}
            >
              <DepartmentSelect allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.location")}
              name="location"
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.placeLocation")}
              name="placeLocation"
              style={{ marginBottom: 12 }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.maintainDays")}
              name="maintainDays"
              style={{ marginBottom: 12 }}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("equipment.maintainThreshold")}
              name="maintainThreshold"
              style={{ marginBottom: 12 }}
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("equipment.purchaseAt")}
              name="purchaseAt"
              style={{ marginBottom: 12 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("equipment.purchasePrice")}
              name="purchasePrice"
              style={{ marginBottom: 12 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("equipment.purchaseCurrency")}
              name="purchaseCurrency"
              style={{ marginBottom: 12 }}
            >
              <CurrencySelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={t("equipment.supplier")}
              name="supplier"
              style={{ marginBottom: 0 }}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EquipmentForm;
